import React, { FC, useEffect, useRef, useState } from 'react';
import {
  Badge,
  BadgePriority as BADGE_PRIORITY,
  Divider,
  Dropdown,
  SectionNotification,
  SectionNotificationType as NOTIFICATION_TYPE,
  Text,
  TextButton,
  DropdownInputHandle,
  Accordion,
} from 'wix-ui-tpa/cssVars';
import { MultiselectDropdown } from '@wix/bookings-viewer-ui';
import {
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import { VideoFillXSmall as CameraSVG } from '@wix/wix-ui-icons-common/on-stage/system';
import { classes, st } from './BookingDetails.st.css';
import {
  BookingDetailsDataHooks,
  CALENDAR_SUMMARY_EXPERIMENT,
} from './constants';
import { useCalendarActions } from '../../Hooks/useCalendarActions';
import { WidgetComponents, WidgetElements } from '../../../../utils/bi/consts';
import { AccessibilityHtmlTags } from '../../../../utils/accessibility/constants';
import { BookingPreference } from '../../../../utils/bookingPreferences/bookingPreferences';
import AlertIcon from '../../../../assets/Icons/notificationAlert.svg';
import { BookingDetailsViewModel } from '../../ViewModel/bookingDetailsViewModel/bookingDetailsViewModel';
import { useCreateCssVarsClassContainer } from '../../Hooks/useCreateCssVarsClassContainer';
import { useSettings } from '@wix/tpa-settings/react';
import { useSettingsParams } from '../../Hooks/useSettingsParams';

export interface BookingDetailsProps {
  viewModel: BookingDetailsViewModel;
}

export type CollapsibleBookingDetailsProps = BookingDetailsProps & {
  isCollapsed: boolean | undefined;
  title: string;
};

const BookingDetails: React.FC<CollapsibleBookingDetailsProps> = ({
  viewModel,
  isCollapsed,
  title,
}) => {
  const { isMobile, isViewer } = useEnvironment();
  const { preferences, alert, serviceName } = viewModel;
  const settings = useSettings();
  const settingsParams = useSettingsParams();
  const { onElementClicked } = useCalendarActions();
  const [isAccordionCollapsed, setAccordionCollapsed] = useState(
    !isViewer ? false : isCollapsed,
  );
  const onAccordionItemClick = (_: any, isExpanded: boolean) => {
    setAccordionCollapsed(!isExpanded);
    onElementClicked(
      WidgetComponents.BOOKING_DETAILS,
      isExpanded ? WidgetElements.EXPAND : WidgetElements.COLLAPSE,
    );
  };

  const bookingPreferences = preferences?.bookingPreferences;

  const shouldShowPreferences = () => {
    return bookingPreferences?.some(
      (bookingPreference: BookingPreference) =>
        bookingPreference.options.length > 1,
    );
  };
  const { t } = useTranslation();
  const { experiments } = useExperiments();
  const isNewCalendarSummaryEnabled = experiments.enabled(
    CALENDAR_SUMMARY_EXPERIMENT,
  );

  const NewCalendarSummary: React.FC = () => {
    return (
      <div className={classes.NewServiceDetailsWrapper}>
        <Text
          data-hook={BookingDetailsDataHooks.SERVICE_DETAILS}
          className={classes.title}
          tagName={settings.get(settingsParams.sectionHeaderHtmlTag)}
        >
          {title}
        </Text>
        <Divider
          className={classes.divider}
          data-hook={BookingDetailsDataHooks.Divider}
        />
        <Text
          data-hook={BookingDetailsDataHooks.SLOT_NAME}
          className={classes.commonTitleStyles}
        >
          {serviceName}
        </Text>
        {!isAccordionCollapsed && (
          <div className={classes.expandedSlotDetails}>
            <TimeSlotDetails viewModel={viewModel} />
          </div>
        )}
        <Accordion
          key={`details-${isAccordionCollapsed}`}
          className={classes.newCalendarSummaryAccordion}
          initiallyExpanded={isAccordionCollapsed ? '' : 'details'}
          onItemClick={onAccordionItemClick}
          data-hook={BookingDetailsDataHooks.SERVICE_DETAILS}
        >
          <Accordion.Item
            id="details"
            title={
              isAccordionCollapsed
                ? t('app.defaults.widget.booking-more-details-button')
                : t('app.defaults.widget.booking-less-details-button')
            }
            className={classes.commonTextStyleAccordionItem}
          />
        </Accordion>
      </div>
    );
  };

  return (
    <div
      data-hook={BookingDetailsDataHooks.BOOKING_DETAILS_SELECTION_WRAPPER}
      className={st(classes.root, {
        isMobile,
        alignment: settings.get(settingsParams.textAlignment),
      })}
    >
      {shouldShowPreferences() && isNewCalendarSummaryEnabled ? (
        <BookingPreferences viewModel={viewModel} showDivider={false} />
      ) : null}
      {isMobile ? (
        isNewCalendarSummaryEnabled ? (
          <NewCalendarSummary />
        ) : (
          <div className={classes.serviceDetailsWrapper}>
            <Accordion
              key={`details-${isCollapsed}`}
              className={classes.accordion}
              initiallyExpanded={isCollapsed ? '' : 'details'}
              onItemClick={onAccordionItemClick}
              data-hook={BookingDetailsDataHooks.SERVICE_DETAILS}
            >
              <Accordion.Item
                id="details"
                title={title}
                titleTag={settings.get(settingsParams.sectionHeaderHtmlTag)}
              >
                <TimeSlotDetails viewModel={viewModel} />
              </Accordion.Item>
            </Accordion>
          </div>
        )
      ) : isNewCalendarSummaryEnabled ? (
        <NewCalendarSummary />
      ) : (
        <div className={classes.serviceDetailsWrapper}>
          <Text
            data-hook={BookingDetailsDataHooks.Title}
            className={classes.title}
            tagName={settings.get(settingsParams.sectionHeaderHtmlTag)}
          >
            {title}
          </Text>
          <Divider
            className={classes.divider}
            data-hook={BookingDetailsDataHooks.Divider}
          />
          <TimeSlotDetails viewModel={viewModel} />
        </div>
      )}

      {shouldShowPreferences() && !isNewCalendarSummaryEnabled ? (
        <BookingPreferences viewModel={viewModel} showDivider={!isMobile} />
      ) : null}
      <Alert alert={alert} />
    </div>
  );
};
export default BookingDetails;

const Alert = ({ alert }: { alert?: string }) => {
  return alert ? (
    <SectionNotification
      type={NOTIFICATION_TYPE.alert}
      data-hook={BookingDetailsDataHooks.ALERT}
      className={classes.alert}
    >
      <SectionNotification.Icon icon={<AlertIcon />} />
      <SectionNotification.Text>{alert}</SectionNotification.Text>
    </SectionNotification>
  ) : null;
};

const TimeSlotDetails: React.FC<BookingDetailsProps> = ({ viewModel }) => {
  const {
    serviceName,
    dateAndTime,
    paymentDescription,
    preferences,
    videoConferenceBadgeText,
    addOnsCount,
  } = viewModel;

  const { experiments } = useExperiments();
  const isNewCalendarSummaryEnabled = experiments.enabled(
    CALENDAR_SUMMARY_EXPERIMENT,
  );
  const bookingPreferences = preferences?.bookingPreferences;
  const PaymentDescription: React.FC = () => {
    return (
      <Text
        data-hook={BookingDetailsDataHooks.SLOT_PLAN_TYPE}
        className={classes.commonTextStyle}
        tagName={AccessibilityHtmlTags.Paragraph}
        key="payment-description"
      >
        {paymentDescription}
      </Text>
    );
  };
  const VideoConferenceBadge: React.FC = () => {
    return (
      <Badge
              data-hook={BookingDetailsDataHooks.VIDEO_CONFERENCE_BADGE}
              priority={BADGE_PRIORITY.light}
              icon={<CameraSVG />}
              className={classes.videoConferenceBadge}
            >
              {videoConferenceBadgeText}
            </Badge>
    );
  };
  const AddOnsCount: React.FC = () => {
    return (
      <Text
        data-hook={BookingDetailsDataHooks.ADDONS_COUNT}
        className={classes.commonTitleStyles}
        tagName={AccessibilityHtmlTags.Paragraph}
        key="add-ons-count"
      >
        {addOnsCount}
      </Text>
    );
  };
  const DateAndTime: React.FC = () => {
    return (
      <Text
        data-hook={BookingDetailsDataHooks.SLOT_DATE_AND_TIME}
        className={
          classes.commonTextStyle
        }
        tagName={AccessibilityHtmlTags.Paragraph}
        key="date-and-time"
      >
        {dateAndTime}
      </Text>
    );
  };
  const BookingPreferences: React.FC = () => {
    return (
      <>
      {!!bookingPreferences &&
        bookingPreferences?.map((bookingPreference) => {
          return bookingPreference.options.length === 1 ? (
            <Text
              data-hook={`${BookingDetailsDataHooks.TIME_SLOT_DETAILS}-${bookingPreference.key}`}
              className={classes.commonTextStyle}
              tagName={AccessibilityHtmlTags.Paragraph}
              aria-label={bookingPreference.options[0].ariaLabel}
              key={bookingPreference.key}
            >
              {bookingPreference.options[0].value}
            </Text>
          ) : null;
        })}
      </>
    );
  };
  const NewTimeSlotDetails: React.FC = () => {
    return (
      <div className={classes.newTimeSlotDetailsWrapper}>
          {videoConferenceBadgeText && <VideoConferenceBadge />}
          {paymentDescription && <PaymentDescription />}
          {addOnsCount && <AddOnsCount />}
          <DateAndTime />
        <BookingPreferences />
      </div>
    );
  };

  return (
    isNewCalendarSummaryEnabled ? <NewTimeSlotDetails /> : (
      <>
      <div
        data-hook={BookingDetailsDataHooks.SLOT_DETAILS}
        className={classes.serviceNameAndDate}
      >
        {videoConferenceBadgeText && <VideoConferenceBadge />}
          <Text
            data-hook={BookingDetailsDataHooks.SLOT_NAME}
            className={classes.commonTitleStyles}
            tagName={AccessibilityHtmlTags.Paragraph}
            key="service-name"
          >
            {serviceName}
          </Text>
        {addOnsCount && <AddOnsCount />}
        <Text
          data-hook={BookingDetailsDataHooks.SLOT_DATE_AND_TIME}
          className={classes.commonTitleStyles}
          tagName={AccessibilityHtmlTags.Paragraph}
          key="date-and-time"
        >
          {dateAndTime}
        </Text>
      </div>
      <BookingPreferences />
      {paymentDescription && <PaymentDescription />}
    </> 
    )
  );
};

type BookingPreferencesProps = BookingDetailsProps & {
  showDivider: boolean;
};

const BookingPreferences: FC<BookingPreferencesProps> = ({
  viewModel,
  showDivider,
}) => {
  const {
    onBookingPreferenceOptionSelected,
    onClearSelectedBookingPreferences,
    onElementClicked,
  } = useCalendarActions();
  const { isRTL } = useEnvironment();
  const [forceDropdownRender, setForceDropdownRender] = useState(false);
  const [isClearButtonVisible, setClearButtonVisibility] = useState(false);
  const [selectedDropDown, setSelectedDropDown] = useState<BookingPreference>();
  const { dateAndTime, preferences } = viewModel;
  const { t } = useTranslation();
  const { experiments } = useExperiments();

  const dropdownRefs = useRef<DropdownInputHandle[]>([]);

  const isCalendarA11YLabelsEnabled = experiments.enabled(
    'specs.bookings.A11YCalendarLabel',
  );
  const isCalendarSummaryEnabled = experiments.enabled(
    CALENDAR_SUMMARY_EXPERIMENT,
  );
  const { rootRef, cssVarsContainerClassName } =
    useCreateCssVarsClassContainer();

  useEffect(() => {
    setClearButtonVisibility(false);
  }, [dateAndTime]);

  useEffect(() => {
    dropdownRefs.current = dropdownRefs.current.slice(
      0,
      preferences?.bookingPreferences.length,
    );
  }, [preferences?.bookingPreferences]);

  const clearDropdowns = () => {
    setForceDropdownRender(!forceDropdownRender);
    onClearSelectedBookingPreferences();
    setClearButtonVisibility(false);
    setFocusOnDropdown();
  };

  const setFocusOnDropdown = () => {
    setTimeout(() => {
      dropdownRefs.current[0]?.focus();
    }, 50);
  };

  const onDropdownChange = (
    selectedOptionId: string,
    bookingPreference: BookingPreference,
    numberOfParticipants: number,
    isMultipleChoices: boolean,
  ) => {
    onBookingPreferenceOptionSelected({
      key: bookingPreference.key,
      value: selectedOptionId,
      numberOfParticipants,
      isMultipleChoices,
    });
    setClearButtonVisibility(true);
  };

  const onDropdownClick = (bookingPreference: BookingPreference) => {
    onElementClicked(
      WidgetComponents.BOOKING_DETAILS,
      WidgetElements.DROPDOWN,
      bookingPreference.key,
    );
    setSelectedDropDown(bookingPreference);
  };

  const getDropdownComponent = (
    bookingPreference: BookingPreference,
    index: number,
  ) => {
    return bookingPreference.isMultipleChoices ? (
      <MultiselectDropdown
        dropdownRef={(el: DropdownInputHandle | null) =>
          (dropdownRefs.current[index] = el!)
        }
        data-hook={
          BookingDetailsDataHooks.DROPDOWN + '-' + bookingPreference.key
        }
        newErrorMessage={isCalendarA11YLabelsEnabled}
        key={dateAndTime + bookingPreference.key + forceDropdownRender}
        className={st(classes.commonMultiselectDropDownStyle, {
          aboveAll: selectedDropDown === bookingPreference,
          isRTL: !!preferences?.appendToViewPort && isRTL,
        })}
        label={
          isCalendarA11YLabelsEnabled ? bookingPreference.label : undefined
        }
        appendTo={preferences?.appendToViewPort ? 'viewport' : undefined}
        optionsContainerClass={cssVarsContainerClassName}
        disabled={bookingPreference.disabled}
        onExpandedChange={() => onDropdownClick(bookingPreference)}
        error={!!bookingPreference.error.message}
        errorMessage={bookingPreference.error.message}
        applyButtonContent={t(
          'app.booking-details.dropdowns.custom-preferences.apply.text',
        )}
        aria-label={bookingPreference.placeholder}
        placeholder={
          isCalendarA11YLabelsEnabled
            ? undefined
            : bookingPreference.placeholder
        }
        note={bookingPreference.note!}
        options={bookingPreference.options.map((option) => ({
          id: option.id!,
          value: option.value!,
          subtitle: option.subtitle!,
          counterOptions: {
            incrementAriaLabel: t(
              'app.booking-details.dropdowns.custom-preferences.increment-aria-label',
              { optionName: option.value },
            ),
            decrementAriaLabel: t(
              'app.booking-details.dropdowns.custom-preferences.decrement-aria-label',
              { optionName: option.value },
            ),
            inputAriaLabel: t(
              'app.booking-details.dropdowns.custom-preferences.input-aria-label',
              { optionName: option.value },
            ),
            max:
              bookingPreference.openSpotsRemained! +
              option.numberOfParticipants!,
            numberOfParticipants: option.numberOfParticipants!,
            onChange: (numberOfParticipants: number) =>
              onDropdownChange(
                option.id!,
                bookingPreference,
                numberOfParticipants,
                true,
              ),
          },
        }))}
      />
    ) : (
      <Dropdown
        newErrorMessage={isCalendarA11YLabelsEnabled}
        label={
          isCalendarA11YLabelsEnabled ? bookingPreference.label : undefined
        }
        upgrade
        ref={(el) => (dropdownRefs.current[index] = el!)}
        initialSelectedId={bookingPreference.preselectedOptionId || '-1'} // WA since ui-tpa does not recognize switching from value to undefined as reset
        error={!!bookingPreference.error.message}
        errorMessage={bookingPreference.error.message}
        data-hook={
          BookingDetailsDataHooks.DROPDOWN + '-' + bookingPreference.key
        }
        className={st(classes.commonDropDownStyle, {
          aboveAll: selectedDropDown === bookingPreference,
          isRTL: !!preferences?.appendToViewPort && isRTL,
        })}
        appendTo={preferences?.appendToViewPort ? 'viewport' : undefined}
        optionsContainerClass={cssVarsContainerClassName}
        disabled={bookingPreference.disabled}
        aria-label={bookingPreference.placeholder}
        placeholder={
          isCalendarA11YLabelsEnabled
            ? undefined
            : bookingPreference.placeholder
        }
        options={bookingPreference.options}
        onExpandedChange={() => onDropdownClick(bookingPreference)}
        key={
          dateAndTime +
          bookingPreference.key +
          forceDropdownRender +
          bookingPreference.preselectedOptionId
        }
        onChange={(selectedOption) =>
          onDropdownChange(selectedOption.id!, bookingPreference, 1, false)
        }
      />
    );
  };

  const hasError = preferences?.bookingPreferences.some(
    (preference) => !!preference.error.message,
  );

  useEffect(() => {
    if (isCalendarA11YLabelsEnabled && hasError) {
      const refIndex = preferences?.bookingPreferences.findIndex(
        (preference) => !!preference.error.message,
      );
      dropdownRefs?.current[refIndex!]?.focus();
    }
  }, [hasError]);

  if (!preferences) {
    return null;
  }

  const { bookingPreferences, titleText, clearText } = preferences;

  return (
    <div
      data-hook={BookingDetailsDataHooks.SLOT_PREFERENCES_WRAPPER}
      ref={rootRef}
    >
      {showDivider && (
        <Divider
          className={classes.divider}
          data-hook={BookingDetailsDataHooks.DIVIDER}
        />
      )}
      <div
        className={
          isCalendarSummaryEnabled
            ? classes.newBookingsPreferences
            : classes.bookingPreferences
        }
      >
        <div
          className={
            isCalendarSummaryEnabled
              ? classes.newPreferencesTitle
              : classes.preferencesTitle
          }
        >
          <Text
            data-hook={BookingDetailsDataHooks.SLOT_PREFERENCES_TITLE}
            className={
              isCalendarSummaryEnabled
                ? classes.title
                : classes.commonTitleStyles
            }
            tagName={AccessibilityHtmlTags.Paragraph}
          >
            {titleText}
          </Text>
        </div>
        {isCalendarSummaryEnabled && (
          <Divider
            className={classes.divider}
            data-hook={BookingDetailsDataHooks.DIVIDER}
          />
        )}
        {bookingPreferences.map(
          (bookingPreference: BookingPreference, index) => {
            if (bookingPreference.options.length > 1) {
              return getDropdownComponent(bookingPreference, index);
            }
            return null;
          },
        )}
        {isClearButtonVisible ? (
          <p className={classes.clearPreferences}>
            <TextButton
              data-hook={BookingDetailsDataHooks.CLEAR_BUTTON}
              className={classes.commonLinkStyles}
              onClick={() => clearDropdowns()}
            >
              <span className={classes.textWithEllipsis}>{clearText}</span>
            </TextButton>
          </p>
        ) : null}
      </div>
    </div>
  );
};
